import React, { useMemo, useState } from "react";

// Per-vertical default checklists. Landscaping equipment (trailer/mower/skid)
// vs. a pest/service truck. A real template editor (settings) comes later; these
// sensible defaults let a tech inspect today.
const PEST_TRUCK = [
  "Tires", "Brakes", "Lights", "Windshield", "Fluids",
  "Sprayer equipment", "Chemical storage secured", "Safety gear (PPE)",
];
const LANDSCAPE_EQUIP = [
  "Tires / tracks", "Blades / cutting deck", "Trailer hitch & chains", "Lights",
  "Fluids / fuel", "Guards in place", "Straps / tie-downs", "Safety gear (PPE)",
];

function defaultItemsForType(type) {
  if (["trailer", "skid", "excavator", "crane"].includes(type)) return LANDSCAPE_EQUIP;
  return PEST_TRUCK;
}

export default function InspectionModal({ vehicle, onSubmit, onClose }) {
  const items = useMemo(() => defaultItemsForType(vehicle?.type), [vehicle?.type]);
  const [checks, setChecks] = useState(() => Object.fromEntries(items.map((i) => [i, true])));
  const [inspectionType, setInspectionType] = useState("pre_trip");
  const [notes, setNotes] = useState("");
  const [odometer, setOdometer] = useState(vehicle?.odometer || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const anyFail = Object.values(checks).some((v) => !v);
  const status = anyFail ? "needs_attention" : "pass";

  const toggle = (item) => setChecks((c) => ({ ...c, [item]: !c[item] }));

  const submit = async (e) => {
    e.preventDefault();
    if (anyFail && !notes.trim()) {
      setErr("Add notes explaining what failed.");
      return;
    }
    setSaving(true);
    setErr("");
    try {
      await onSubmit({
        vehicle: vehicle.id,
        inspection_type: inspectionType,
        status,
        checklist_data: checks,
        notes: notes || "",
        odometer_reading: odometer ? parseInt(odometer, 10) : undefined,
      });
      onClose();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Failed to submit inspection.");
      setSaving(false);
    }
  };

  return (
    <div className="modal" role="dialog" aria-modal="true">
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-card" role="document">
        <div className="modal-title">
          <h3>Inspect {vehicle?.name}</h3>
          <p className="muted">Pre/post-trip checklist. Fail anything and note why.</p>
        </div>
        <form onSubmit={submit} className="fleet-form">
          <div className="grid grid--2">
            <label>Type
              <select value={inspectionType} onChange={(e) => setInspectionType(e.target.value)}>
                <option value="pre_trip">Pre-Trip</option>
                <option value="post_trip">Post-Trip</option>
              </select>
            </label>
            <label>Odometer
              <input type="number" value={odometer} onChange={(e) => setOdometer(e.target.value)} />
            </label>
          </div>

          <div className="inspect-items">
            {items.map((item) => (
              <button
                type="button"
                key={item}
                className={`inspect-item ${checks[item] ? "ok" : "fail"}`}
                onClick={() => toggle(item)}
              >
                <span>{item}</span>
                <span className="inspect-flag">{checks[item] ? "Pass" : "Fail"}</span>
              </button>
            ))}
          </div>

          <label>Notes{anyFail ? " (required — what failed?)" : ""}
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
          </label>

          <div className="row-between">
            <span className={`inventory-pill ${anyFail ? "warn" : "success"}`}>
              Result: {status === "pass" ? "Pass" : "Needs attention"}
            </span>
            {err && <span className="error-text">{err}</span>}
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="secondary">Cancel</button>
            <button type="submit" disabled={saving}>{saving ? "Submitting…" : "Submit Inspection"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
